import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Type } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { allTrackers } from "app/services/track/track";

import { TRACKING_EVENT_NAME } from "../constants";
import type { ConfigurationState } from "../types";

import { DevicePicker } from "./DevicePicker";
import { LocationPicker } from "./LocationPicker";
import { TruncatedNameText, VerticalDivider, WebsiteIcon } from "./styles";

type Props = {
    favicon: string;
    campaignName: string;
    configuration: ConfigurationState;
};

export const ConfigurationHeader = ({ favicon, campaignName, configuration }: Props) => {
    const { translate } = useTranslate();
    const { selectedDevice, selectedLocationIds, deviceOptions, locationOptions, setDevice, setLocations } =
        configuration;

    const handleDeviceSelect = useCallback(
        (...args: Parameters<typeof setDevice>) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `${TRACKING_EVENT_NAME}/Device changed`);
            setDevice(...args);
        },
        [setDevice],
    );

    const handleLocationsChange = useCallback(
        (...args: Parameters<typeof setLocations>) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `${TRACKING_EVENT_NAME}/Locations changed`);
            setLocations(...args);
        },
        [setLocations],
    );

    return (
        <Flex
            alignItems="center"
            gap="8px"
        >
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[400]}
            >
                {translate("rankTracker.addKeywords.modal.configuration")}
            </Text>

            <Flex
                alignItems="center"
                gap="8px"
                minWidth="0"
            >
                <WebsiteIcon
                    iconSrc={favicon}
                    iconType={Type.Website}
                />
                <TruncatedNameText fontVariant="value">{campaignName}</TruncatedNameText>
            </Flex>

            <VerticalDivider />

            <DevicePicker
                selectedDevice={selectedDevice}
                deviceOptions={deviceOptions}
                onDeviceSelect={handleDeviceSelect}
            />

            <VerticalDivider />

            <LocationPicker
                selectedLocationIds={selectedLocationIds}
                locationOptions={locationOptions}
                onLocationsChange={handleLocationsChange}
            />
        </Flex>
    );
};
